import {
  useEffect,
  useEffectEvent,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { useGT } from 'gt-react';
import type { z } from 'zod';
import { normalizeWorkflowEditTrail } from '@erp/workflows';
import { useUndoTree } from '@/lib/useUndoTree';
import { activePathTrail, currentNode, type UndoStep } from '@/lib/undoTree';
import {
  historySnapshot,
  loadWorkflowEditorHistory,
  saveWorkflowEditorHistory,
  sameHistorySnapshot,
  workflowHistoryStep,
  WORKFLOW_HISTORY_MAX_NODES,
  type GraphSnapshot,
  type WorkflowHistoryIntent,
  type HistoryTranslate,
} from '../workflowEditorHistory';

type WorkflowHistorySession<S extends GraphSnapshot> = {
  draftId: string;
  baseline: S;
  schema: z.ZodType<NoInfer<S>>;
  surface?: 'editor' | 'group';
};

function useWorkflowHistorySession<S extends GraphSnapshot>(
  value: S,
  isCreateMode: boolean,
  session?: WorkflowHistorySession<S>,
) {
  const key = session
    ? `${session.draftId}:${session.surface ?? 'editor'}`
    : 'temporary';
  const load = () =>
    session
      ? loadWorkflowEditorHistory(
          session.draftId,
          historySnapshot(session.baseline),
          session.schema,
          session.surface,
        )
      : null;
  const [kept, setKept] = useState(() => ({ key, history: load() }));
  const active = kept.key === key ? kept : { key, history: load() };
  const snapshot = historySnapshot(value);
  const [saved, setSaved] = useState<S | null>(
    () =>
      active.history?.saved ??
      (isCreateMode ? null : structuredClone(snapshot)),
  );
  if (active !== kept) {
    setKept(active);
    setSaved(
      active.history?.saved ??
        (isCreateMode ? null : structuredClone(snapshot)),
    );
  }
  return { key, restored: active.history, snapshot, saved, setSaved };
}

function describePendingEdit<S extends GraphSnapshot>(
  base: S,
  next: S,
  intent: WorkflowHistoryIntent | null,
  epoch: number,
  gt: HistoryTranslate,
): Omit<UndoStep<S>, 'at'> | undefined {
  if (!intent || intent.kind === 'elsewhere') return undefined;
  const step = workflowHistoryStep(base, next, intent, gt);
  return {
    ...step,
    group: step.group === undefined ? undefined : `${step.group}:${epoch}`,
    relabel: (parent) => workflowHistoryStep(parent, next, intent, gt).label,
  };
}

function useRestoredWorkflowSnapshot<S extends GraphSnapshot>(
  key: string,
  restored: ReturnType<typeof loadWorkflowEditorHistory<S>>,
  snapshot: S,
  apply: (state: S) => void,
): S {
  const applyRestored = useEffectEvent(apply);
  const [restoration, setRestoration] = useState(() => ({
    key,
    pending: Boolean(restored),
  }));
  const active =
    restoration.key === key ? restoration : { key, pending: Boolean(restored) };
  const restoredSnapshot = restored ? currentNode(restored.tree).state : null;
  const pending =
    active.pending &&
    restoredSnapshot !== null &&
    !sameHistorySnapshot(snapshot, restoredSnapshot);
  if (active !== restoration || active.pending !== pending)
    setRestoration({ key, pending });
  useLayoutEffect(() => {
    if (restored) applyRestored(currentNode(restored.tree).state);
  }, [key, restored]);
  return pending && restoredSnapshot ? restoredSnapshot : snapshot;
}

function usePersistWorkflowHistory<S extends GraphSnapshot>(
  session: WorkflowHistorySession<S> | undefined,
  tree: Parameters<typeof saveWorkflowEditorHistory<S>>[1],
  saved: S | null,
  enabled: boolean,
) {
  const draftId = session?.draftId;
  const surface = session?.surface;
  useEffect(() => {
    if (draftId && enabled)
      saveWorkflowEditorHistory(draftId, tree, saved, surface);
  }, [draftId, surface, tree, saved, enabled]);
}

function savedHistorySnapshot<S extends GraphSnapshot>(
  value: S | undefined,
  fallback: S,
): S {
  return structuredClone(historySnapshot(value ?? fallback));
}

/** Adapts the editor's pre-mutation checkpoints to the shared branching tree. */
export function useWorkflowUndoTree<S extends GraphSnapshot>({
  value,
  apply,
  isCreateMode = false,
  enabled = true,
  suspended = false,
  session,
}: {
  value: S;
  apply: (snapshot: S) => void;
  isCreateMode?: boolean;
  enabled?: boolean;
  suspended?: boolean;
  session?: WorkflowHistorySession<S>;
}) {
  const gt = useGT();
  const { key, restored, snapshot, saved, setSaved } =
    useWorkflowHistorySession(value, isCreateMode, session);
  const [mergeEpoch, setMergeEpoch] = useState(0);
  const [pending, setPending] = useState<WorkflowHistoryIntent | null>(null);
  const [observed, setObserved] = useState(() => ({ key, snapshot }));
  if (
    observed.key !== key ||
    !sameHistorySnapshot(observed.snapshot, snapshot)
  ) {
    setObserved({ key, snapshot });
    setPending(null);
  }
  const shownSnapshot = useRestoredWorkflowSnapshot(
    key,
    restored,
    snapshot,
    apply,
  );
  const history = useUndoTree<S>({
    value: shownSnapshot,
    apply: (state) => apply(structuredClone(state)),
    capture: structuredClone,
    equals: sameHistorySnapshot,
    openedLabel: gt('Opened draft', {
      context: 'workflow editor history root',
    }),
    elsewhereLabel: gt('Changed elsewhere', {
      context: 'workflow editor edit history',
    }),
    restored: restored?.tree,
    maxNodes: WORKFLOW_HISTORY_MAX_NODES,
    enabled: enabled && !suspended,
    sessionKey: key,
    describeChange: (base, next) =>
      describePendingEdit(base, next, pending, mergeEpoch, gt),
  });
  const latest = useRef({ history, snapshot });
  useLayoutEffect(() => {
    latest.current = { history, snapshot };
  }, [history, snapshot]);
  usePersistWorkflowHistory(
    session,
    history.tree,
    saved,
    enabled && !suspended,
  );

  const actions = {
    pushSnapshot: (intent: WorkflowHistoryIntent) => setPending(intent),
    markSaved: (state?: S) => {
      setMergeEpoch((epoch) => epoch + 1);
      setSaved(savedHistorySnapshot(state, latest.current.snapshot));
    },
    resetHistory: (state: S) => {
      setPending(null);
      setMergeEpoch((epoch) => epoch + 1);
      const next = historySnapshot(state);
      latest.current.history.reset(next);
      setSaved(isCreateMode ? null : next);
    },
    getEditTrail: () =>
      normalizeWorkflowEditTrail(activePathTrail(latest.current.history.tree)),
  };
  return {
    history,
    ...actions,
    undo: history.undo,
    redo: history.redo,
    canUndo: history.canUndo,
    canRedo: history.canRedo,
    isDirtyVsSaved:
      saved === null ||
      !sameHistorySnapshot(saved, currentNode(history.tree).state),
  };
}
